import { Person } from "../../common/models/schema/UpdatePersonResponse";
import { Box, Typography, Grid, Card, CardContent } from "@mui/material";

type PersonDetailsProps = {
  person: Person;
};

const PersonDetails = (props: PersonDetailsProps) => {
  const { person } = props;
  const attributes = person.attributes;

  // Helper function to render field if not null/empty
  const renderField = (label: string, value: any) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}
        </Typography>
      </Grid>
    );
  };

  // Helper function to format currency
  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Person Details
      </Typography>

      {/* Basic Info Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            {renderField("ID", person.id)}
            {renderField("Type", person.type)}
            {renderField("Full Name", attributes.full_name)}
            {renderField("First Name", attributes.first_name)}
            {renderField("Last Name", attributes.last_name)}
            {renderField("Middle Name", attributes.middle_name)}
            {renderField("Email", attributes.email)}
            {renderField("Phone Number", attributes.phone_number)}
            {renderField("Mobile Number", attributes.mobile_number)}
            {renderField("Work Phone", attributes.work_phone_number)}
            {renderField("Fax Number", attributes.fax_number)}
          </Grid>
        </CardContent>
      </Card>

      {/* Employment Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Employment
          </Typography>
          <Grid container spacing={2}>
            {renderField("Employer", attributes.employer)}
            {renderField("Occupation", attributes.occupation)}
            {attributes.custom_values?.position &&
              renderField("Position", attributes.custom_values.position)}
          </Grid>
        </CardContent>
      </Card>

      {/* Contact Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Contact Preferences
          </Typography>
          <Grid container spacing={2}>
            {renderField("Do Not Call", attributes.do_not_call)}
            {renderField("Do Not Contact", attributes.do_not_contact)}
            {renderField("Email Opt In", attributes.email_opt_in)}
            {renderField("Mobile Opt In", attributes.mobile_opt_in)}
            {renderField("Federal Do Not Call", attributes.federal_donotcall)}
          </Grid>
        </CardContent>
      </Card>

      {/* Personal Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Personal Details
          </Typography>
          <Grid container spacing={2}>
            {renderField(
              "Born At",
              attributes.born_at ? formatDate(attributes.born_at) : null,
            )}
            {renderField("Sex", attributes.sex)}
            {renderField("Ethnicity", attributes.ethnicity)}
            {renderField("Marital Status", attributes.marital_status)}
            {renderField("Religion", attributes.religion)}
            {renderField("Language", attributes.language)}
            {renderField("Locale", attributes.locale)}
          </Grid>
        </CardContent>
      </Card>

      {/* Financial Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Financial Information
          </Typography>
          <Grid container spacing={2}>
            {attributes.capital_amount_in_cents > 0 &&
              renderField(
                "Capital Amount",
                formatCurrency(attributes.capital_amount_in_cents),
              )}
            {attributes.donations_amount_in_cents > 0 &&
              renderField(
                "Donations Amount",
                formatCurrency(attributes.donations_amount_in_cents),
              )}
            {attributes.donations_count > 0 &&
              renderField("Donations Count", attributes.donations_count)}
            {attributes.received_capital_amount_in_cents > 0 &&
              renderField(
                "Received Capital",
                formatCurrency(attributes.received_capital_amount_in_cents),
              )}
            {attributes.spent_capital_amount_in_cents > 0 &&
              renderField(
                "Spent Capital",
                formatCurrency(attributes.spent_capital_amount_in_cents),
              )}
          </Grid>
        </CardContent>
      </Card>

      {/* Status Flags */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Status
          </Typography>
          <Grid container spacing={2}>
            {renderField("Is Volunteer", attributes.is_volunteer)}
            {renderField("Is Supporter", attributes.is_supporter)}
            {renderField("Is Donor", attributes.is_donor)}
            {renderField("Is Fundraiser", attributes.is_fundraiser)}
            {renderField("Is Prospect", attributes.is_prospect)}
            {renderField("Is Deceased", attributes.is_deceased)}
            {renderField("Profile Private", attributes.is_profile_private)}
            {renderField(
              "Profile Searchable",
              attributes.is_profile_searchable,
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Custom Values */}
      {attributes.custom_values &&
        Object.values(attributes.custom_values).some((v) => v !== null) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Custom Information
              </Typography>
              <Grid container spacing={2}>
                {renderField(
                  "Custom Page",
                  attributes.custom_values.custom_page,
                )}
                {renderField(
                  "LinkedIn URL",
                  attributes.custom_values.linkedin_url,
                )}
                {renderField("University", attributes.custom_values.university)}
                {renderField("Major", attributes.custom_values.major)}
                {renderField(
                  "Graduation Year",
                  attributes.custom_values.graduation_year,
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

      {/* Notes */}
      {attributes.note && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <Typography variant="body2">{attributes.note}</Typography>
            {attributes.note_updated_at && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                Updated: {formatDate(attributes.note_updated_at)}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timestamps */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>
          <Grid container spacing={2}>
            {renderField("Created At", formatDate(attributes.created_at))}
            {renderField("Updated At", formatDate(attributes.updated_at))}
            {renderField(
              "First Supporter At",
              attributes.first_supporter_at
                ? formatDate(attributes.first_supporter_at)
                : null,
            )}
            {renderField("Signup Type", attributes.signup_type)}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PersonDetails;
